import api, { ApiResponse } from './axiosInstance';

// Dashboard data interfaces
export interface SystemStats {
  totalUsers: number;
  activeUsers: number;
  totalAssets: number;
  windAssets: number;
  solarAssets: number;
  totalProjects: number;
  activeProjects: number;
  completedProjects: number;
  systemHealth: number;
  securityScore: number;
  pendingApprovals: number;
  recentActivities: number;
}

export interface UserGrowthData {
  month: string;
  year: number;
  users: number;
  growth: number;
  newUsers: number;
  activeUsers: number;
  retention: number;
  engagement: number;
}

export interface AssetDistribution {
  name: string;
  value: number;
  color: string;
  status: 'operational' | 'maintenance' | 'fault' | 'inactive';
}

export interface ProjectStatus {
  status: string;
  count: number;
  percentage: number;
}

export interface RecentActivity {
  id: string;
  type: 'user_created' | 'asset_updated' | 'role_modified' | 'project_created' | 'security_alert' | 'task_completed' | 'checkpoint_verified';
  message: string;
  time: string;
  status: 'success' | 'warning' | 'error' | 'info';
  userId?: string;
  targetId?: string;
  targetType?: 'user' | 'asset' | 'project' | 'task' | 'checkpoint';
}

export interface TaskAnalytics {
  totalTasks: number;
  completedTasks: number;
  inProgressTasks: number;
  pendingTasks: number;
  overdueTasks: number;
  completionRate: number;
  avgCompletionTime: number; // in hours
}

export interface CheckpointAnalytics {
  totalCheckpoints: number;
  verifiedCheckpoints: number;
  pendingCheckpoints: number;
  failedCheckpoints: number;
  verificationRate: number;
  byProjectType: Array<{
    projectType: string;
    total: number;
    verified: number;
    pending: number;
  }>;
}

export interface PerformanceMetrics {
  avgResponseTime: number;
  uptime: number;
  errorRate: number;
  activeConnections: number;
  memoryUsage: number;
  cpuUsage: number;
}

export interface UserAnalytics {
  byRole: Array<{
    role: string;
    count: number;
    activeCount: number;
  }>;
  loginActivity: Array<{
    date: string;
    logins: number;
    uniqueUsers: number;
  }>;
  retention: {
    daily: number;
    weekly: number;
    monthly: number;
  };
}

export interface DashboardData {
  systemStats: SystemStats;
  userGrowthData: UserGrowthData[];
  assetDistribution: AssetDistribution[];
  projectStatus: ProjectStatus[];
  recentActivities: RecentActivity[];
  taskAnalytics: TaskAnalytics;
  checkpointAnalytics: CheckpointAnalytics;
  performanceMetrics: PerformanceMetrics;
  userAnalytics: UserAnalytics;
  lastUpdated: string;
}

// Manager/Head Dashboard Interfaces
export interface ManagerDashboardKPIs {
  totalCustomers: {
    value: number;
    thisMonth: number;
  };
  activeProjects: {
    value: number;
    pendingApprovals: number;
  };
  totalLocations: {
    value: number;
    statesCount: number;
  };
  failCases: {
    value: number;
    resolvedToday: number;
  };
}

export interface YearlyPerformanceData {
  year: number;
  actual: number;
  planned: number;
}

export interface YearlyPerformanceAnalysis {
  preventiveMaintenance: YearlyPerformanceData[];
  moduleCleaning: YearlyPerformanceData[];
  grassCutting: YearlyPerformanceData[];
  totalTasks: YearlyPerformanceData[];
}

export interface PendingApproval {
  id: string;
  requesterName: string;
  requesterAvatar?: string;
  description: string;
  projectName: string;
  projectId: string;
  status: 'pending' | 'resubmitted';
  submittedAt: string;
  relativeTime: string;
}

export interface ManagerRecentActivity {
  id: string;
  type: 'project_added' | 'issue_resolved' | 'issue_reported' | 'team_member_added';
  message: string;
  user: string;
  project: string;
  relativeTime: string;
}

export type ProjectHealthStatus = 'good' | 'attention' | 'critical';

export interface ProjectHealthItem {
  projectId: string;
  code: string;
  projectName: string;
  projectManager: string;
  location: string;
  priority: string;
  progress: number;
  totalTasks: number;
  passTasks: number;
  failTasks: number;
  overdueTasks: number;
  skipTasks: number;
  pendingTasks: number;
  inProgressTasks: number;
  resourceCount: number;
  openIssues: number;
  maintenanceScore: number;
  healthStatus: ProjectHealthStatus;
  lastActivityAt: string | null;
}

export interface ProjectStatusBreakdown {
  good: number;
  attention: number;
  critical: number;
  total: number;
}

export interface ResourceLoadItem {
  projectId: string;
  projectName: string;
  resourceCount: number;
  taskLoad: number;
  openTasks: number;
  utilization: number;
}

export interface MaintenanceAggregateStats {
  totalPass: number;
  totalFail: number;
  totalOverdue: number;
  totalSkipped: number;
  totalInProgress: number;
  totalPending: number;
  totalOpenIssues: number;
  passRate: number;
  failRate: number;
}

/** Rich snapshot for Head dashboard (dummy / API extension) */
export interface HeadTodayMetrics {
  issuesOpenedToday: number;
  issuesResolvedToday: number;
  issuesOpenBacklog: number;
  dprResponsesToday: number;
  tasksCompletedToday: number;
  checkpointsVerifiedToday: number;
}

export interface HeadActiveProjectRow {
  id: string;
  name: string;
  site: string;
  status: 'Active' | 'On track' | 'At risk' | 'Planning';
  progressPct: number;
}

export interface HeadWorkDoneItem {
  id: string;
  summary: string;
  projectName: string;
  completedBy: string;
  relativeTime: string;
}

export interface HeadDashboardSnapshot {
  today: HeadTodayMetrics;
  activeProjects: HeadActiveProjectRow[];
  workDoneToday: HeadWorkDoneItem[];
  projectStatusSlices: Array<{ label: string; value: number; color: string }>;
  issuePrioritySlices: Array<{ label: string; value: number; color: string }>;
  dprLast7Days: Array<{ dayLabel: string; count: number }>;
}

export interface UserManagementDashboardData {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  newUsersThisMonth: number;
  newUsersThisYear: number;
  emailVerifiedUsers: number;
  usersWithRecentLogin: number;
  recentActivity: RecentActivity[];
  lastUpdated: string;
}

export interface ManagerDashboardData {
  kpis: ManagerDashboardKPIs;
  yearlyPerformance: YearlyPerformanceAnalysis;
  pendingApprovals: PendingApproval[];
  recentActivities: ManagerRecentActivity[];
  projects: ProjectHealthItem[];
  projectStatusBreakdown: ProjectStatusBreakdown;
  resourceLoadByProject: ResourceLoadItem[];
  criticalProjects: ProjectHealthItem[];
  maintenanceStats: MaintenanceAggregateStats;
  lastUpdated: string;
  /** Extra Head-dashboard panels; same demo payload includes this when using `getManagerDashboard` demo. */
  headSnapshot?: HeadDashboardSnapshot;
}

/** Head dashboard: monthly performance, task breakdown, recent activity */
export interface HeadDashboardTaskType {
  total: number;
  completed: number;
  inProgress: number;
  failed: number;
  completionRate: number;
}

export interface HeadDashboardYearlyItem {
  month: string;
  completedTasks: number;
  failedTasks: number;
  completionRate: number;
}

export interface HeadDashboardData {
  totalCustomers: number;
  activeProjects: number;
  totalLocations: number;
  failCases: number;
  totalTasks: number;
  pendingApprovals: number;
  yearlyPerformance: HeadDashboardYearlyItem[];
  preventiveMaintenance: HeadDashboardTaskType;
  moduleCleaning: HeadDashboardTaskType;
  grassCutting: HeadDashboardTaskType;
  recentActivity: RecentActivity[];
  lastUpdated: string;
}

/** Admin dashboard: Manager & Head activity tracking */
export type AdminActivityType = 'login' | 'project_created' | 'report_generated' | 'approval_submitted';

export interface AdminActivityItem {
  id: string;
  type: AdminActivityType;
  userId: string;
  userName: string;
  userRole: string;
  message: string;
  timestamp: string;
  relativeTime: string;
  metadata?: {
    projectId?: string;
    projectName?: string;
    reportType?: string;
    fileName?: string;
  };
}

export interface ManagerHeadUserSummary {
  userId: string;
  name: string;
  email: string;
  roleName: string;
  lastLoginAt: string | null;
  isOnline: boolean;
  projectsCreatedCount: number;
  reportsGeneratedCount: number;
}

export interface AdminManagerHeadActivity {
  counts: {
    totalManagerHead: number;
    onlineNow: number;
    loginsToday: number;
    loginsThisWeek: number;
    projectsCreatedToday: number;
    projectsCreatedThisWeek: number;
    reportsGeneratedToday: number;
    reportsGeneratedThisWeek: number;
  };
  activityByDay: Array<{
    date: string;
    logins: number;
    projectsCreated: number;
    reportsGenerated: number;
  }>;
  recentActivity: AdminActivityItem[];
  userSummaries: ManagerHeadUserSummary[];
  lastUpdated: string;
}

/** ===== Advanced Manager Dashboard (v2) ===== */

export type ManagerOverviewBucket =
  | 'onboarded'
  | 'live'
  | 'active'
  | 'inactive'
  | 'highPending'
  | 'smooth';

export type ManagerHealthLevel = 'good' | 'attention' | 'critical' | 'notStarted';

export interface ManagerOverviewKPI {
  bucket: ManagerOverviewBucket;
  label: string;
  value: number;
  health: 'good' | 'attention' | 'critical';
  description: string;
}

export interface ManagerOverview {
  kpis: ManagerOverviewKPI[];
  notStartedCount: number;
  thresholds: {
    inactiveDays: number;
    highPendingThreshold: number;
    smoothMaxPending: number;
    smoothMaxStaleDays: number;
  };
  lastUpdated: string;
}

export interface WeeklyProgressPoint {
  weekStart: string;
  label: string;
  totalTasks: number;
  completed: number;
  failed: number;
  inProgress: number;
  pending: number;
  /** Tasks auto-skipped because the next same-frequency task came due. */
  skipped?: number;
  completionRate: number;
}

export interface WeeklyProgressResponse {
  filters: {
    projectIds: string[];
    locationIds: string[];
    departmentIds: string[];
    weeks: number;
  };
  series: WeeklyProgressPoint[];
  totals: {
    totalTasks: number;
    completed: number;
    failed: number;
    pending: number;
    completionRate: number;
  };
  byDepartment: Array<{
    departmentId: string;
    department: string;
    totalTasks: number;
    completed: number;
    completionRate: number;
  }>;
  lastUpdated: string;
}

export interface EngineerActivityLog {
  engineerId: string;
  engineerName: string;
  lastUploadAt: string | null;
  uploadsLast7d: number;
  passLast7d: number;
  failLast7d: number;
  pendingNow: number;
  status: 'active' | 'idle' | 'inactive';
}

export interface DrillDownProject {
  projectId: string;
  code: string;
  projectName: string;
  customer: string;
  location: string;
  department: string;
  manager: string;
  priority: string;
  progress: number;
  status: 'active' | 'inactive';
  approvalStatus: string;
  totalTasks: number;
  completedTasks: number;
  pendingTasks: number;
  failedTasks: number;
  overdueTasks: number;
  inProgressTasks: number;
  resourceCount: number;
  lastActivityAt: string | null;
  daysSinceLastUpdate: number | null;
  health: ManagerHealthLevel;
  engineers: EngineerActivityLog[];
}

export interface DrillDownResponse {
  bucket: ManagerOverviewBucket;
  bucketLabel: string;
  total: number;
  projects: DrillDownProject[];
  byDepartment: Array<{
    department: string;
    count: number;
    completionRate: number;
  }>;
  lastUpdated: string;
}

export type ManagerChatbotIntent =
  | 'inactive-projects'
  | 'highest-pending'
  | 'weekly-summary'
  | 'no-updates-2-days'
  | 'smooth-projects'
  | 'critical-projects';

export interface ManagerChatbotItem {
  title: string;
  subtitle?: string;
  badge?: string;
  meta?: string;
  projectId?: string;
}

export interface ManagerChatbotResponse {
  intent: ManagerChatbotIntent;
  headline: string;
  summary: string;
  items: ManagerChatbotItem[];
  metric?: { label: string; value: string };
  generatedAt: string;
}

export interface ManagerFilters {
  projectIds?: string[];
  locationIds?: string[];
  departmentIds?: string[];
}

/** ===== OMS Manager Dashboard — 4-Layer ===== */

export type OmsLayerKpiHealth = 'good' | 'attention' | 'critical' | 'neutral';

export type OmsLayer1Bucket =
  | 'omsTotal'
  | 'omsApproved'
  | 'omsPendingApproval'
  | 'omsDrafts';

export type OmsLayer2Bucket =
  | 'omsStarted'
  | 'omsReadyToStart'
  | 'omsReadyToSendReview';

export type OmsResourceBucket =
  | 'omsResourceTotal'
  | 'omsResourceWorking'
  | 'omsResourceInactive';

export type OmsDrillBucket = OmsLayer1Bucket | OmsLayer2Bucket | OmsResourceBucket;

export interface OmsLayerKpi<T extends string = string> {
  bucket: T;
  label: string;
  value: number;
  description: string;
  health: OmsLayerKpiHealth;
}

export interface OmsLayeredOverview {
  layer1: OmsLayerKpi<OmsLayer1Bucket>[];
  layer2: {
    approved: OmsLayerKpi<'omsApproved'>;
    active: OmsLayerKpi<'omsStarted'>;
    /**
     * Approved − Started remainder (tasks not generated yet), so the
     * pipeline tallies: Approved = Started + Ready to Start. Optional to
     * tolerate a backend that predates the field.
     */
    readyToStart?: OmsLayerKpi<'omsReadyToStart'>;
    /** Fully configured drafts awaiting review submission. */
    readyToSendReview: OmsLayerKpi<'omsReadyToSendReview'>;
  };
  rejectedCount: number;
  thresholds: { inactiveDays: number };
  lastUpdated: string;
}

export interface OmsExecutionProjectRow {
  projectId: string;
  code: string;
  projectName: string;
  passTasks: number;
  failTasks: number;
  holdTasks: number;
  dueTasks: number;
  inProgressTasks: number;
  totalTracked: number;
}

export interface OmsExecutionOverview {
  projects: OmsExecutionProjectRow[];
  totals: {
    passTasks: number;
    failTasks: number;
    holdTasks: number;
    dueTasks: number;
    inProgressTasks: number;
  };
  lastUpdated: string;
}

export interface OmsProjectPickerItem {
  id: string;
  name: string;
  code: string;
  /** Location the project belongs to (drives cascading location→project filters). */
  locationId: string | null;
}

/** Task counts by status ('unassigned' folds into due on the backend). */
export interface OmsResourceStatusCounts {
  pass: number;
  fail: number;
  inprogress: number;
  due: number;
  overdue: number;
  hold: number;
}

export interface OmsResourceProjectSegment {
  projectId: string;
  projectName: string;
  completedTasks: number;
  passTasks: number;
  failTasks: number;
  pendingTasks: number;
  statusCounts: OmsResourceStatusCounts;
}

export interface OmsResourceRow {
  resourceId: string;
  resourceName: string;
  roleName: string;
  supervisorId: string | null;
  totalCompleted: number;
  totalPending: number;
  statusCounts: OmsResourceStatusCounts;
  lastSubmittedAt: string | null;
  daysSinceLastSubmit: number | null;
  isWorking: boolean;
  hasEverSubmitted: boolean;
  projects: OmsResourceProjectSegment[];
}

export interface OmsStartedProject {
  projectId: string;
  projectName: string;
  projectCode: string;
  totalTasks: number;
  completedTasks: number;
  pendingTasks: number;
  overdueTasks: number;
  assignedResourceCount: number;
}

export type OmsResourcesRange = 'today' | 'week' | 'month' | 'all' | 'custom';

export interface OmsResourcesOverview {
  kpis: OmsLayerKpi<OmsResourceBucket>[];
  resources: OmsResourceRow[];
  startedProjects: OmsStartedProject[];
  range: OmsResourcesRange;
  rangeStart: string | null;
  /** Non-null only for range='custom' (inclusive end of the window). */
  rangeEnd: string | null;
  thresholds: { inactiveDays: number };
  lastUpdated: string;
}

export interface OmsApprovalsSummary {
  /** Total pending project approvals (not capped). */
  pendingCount: number;
  /** Top pending approvals (capped at 10 by the backend). */
  items: PendingApproval[];
}

/**
 * One geo-tagged site cluster for the Command Center map — active OMS
 * projects rolled up by location. Only locations with valid coordinates.
 */
export interface OmsMapLocation {
  locationId: string;
  name: string;
  /** State/region of the site (null when the location has none). */
  state: string | null;
  latitude: number;
  longitude: number;
  projectCount: number;
  /** Distinct engineers assigned to tasks at this location. */
  resourceCount: number;
  /** Summed installed/planned/AC capacity in MW (0 = unknown). */
  capacityMW: number;
  tasks: {
    total: number;
    completed: number;
    pending: number;
    overdue: number;
    failed: number;
  };
  health: OmsLayerKpiHealth;
  topProjects: string[];
}

/** One project row on the per-location dashboard. */
export interface OmsLocationProjectRow {
  projectId: string;
  code: string;
  projectName: string;
  customer: string;
  capacityMW: number;
  acCapacity: string;
  dcCapacity: string;
  progress: number;
  resourceCount: number;
  statusCounts: OmsResourceStatusCounts;
  totalTasks: number;
  health: OmsLayerKpiHealth;
}

/** Per-location OMS dashboard payload. */
export interface OmsLocationOverview {
  location: {
    locationId: string;
    name: string;
    latitude: number | null;
    longitude: number | null;
    city: string | null;
    state: string | null;
  };
  projects: OmsLocationProjectRow[];
  resources: OmsResourceRow[];
  totals: {
    projectCount: number;
    resourceCount: number;
    capacityMW: number;
    tasks: { total: number; completed: number; pending: number; overdue: number; failed: number };
  };
  lastUpdated: string;
}

/**
 * Consolidated OMS Operations Dashboard payload (unified Manager + Head).
 * Every slice is nullable — a failed backend composer yields null for its
 * slice, never a failed request. `approvals` is non-null only for Head roles.
 */
export interface OmsOverviewResponse {
  kpis: OmsLayeredOverview | null;
  execution: OmsExecutionOverview | null;
  resources: OmsResourcesOverview | null;
  trends: {
    weekly: WeeklyProgressResponse | null;
    yearly: YearlyPerformanceAnalysis | null;
  };
  approvals: OmsApprovalsSummary | null;
  map: OmsMapLocation[] | null;
  range: OmsResourcesRange;
  generatedAt: string;
}

export interface OmsDrillDownResponse {
  bucket: OmsDrillBucket;
  bucketLabel: string;
  total: number;
  projects: DrillDownProject[];
  resources?: OmsResourceRow[];
  lastUpdated: string;
}

/* ==================== Admin Analytics (platform health) ==================== */

/** Lightweight project row for growth + drill joins. */
export interface AdminAnalyticsProject {
  projectId: string;
  name: string;
  code: string;
  locationId: string | null;
  locationName: string | null;
  state: string | null;
  capacityMW: number;
  createdAt: string;
  approvalStatus: string;
}

/** One engineer with platform-lifecycle timestamps. */
export interface AdminAnalyticsEngineer {
  engineerId: string;
  name: string;
  roleName: string;
  /** User account creation — proxy for onboarding date. */
  onboardedAt: string;
  firstSubmissionAt: string | null;
  lastActiveAt: string | null;
}

/** One day of app activity for one engineer on one project (IST days). */
export interface AdminDailyActivity {
  date: string;
  engineerId: string;
  projectId: string;
  actions: number;
  completed: number;
  skipped: number;
}

/** Peak-pulse cell: actions at (ISO weekday 1–7, IST hour 0–23). */
export interface AdminPeakCell {
  dow: number;
  hour: number;
  actions: number;
}
export interface AdminPeakByState extends AdminPeakCell {
  state: string | null;
}
export interface AdminPeakByEngineer extends AdminPeakCell {
  engineerId: string;
}

/** One day of task flow for one project (see backend interface docs). */
export interface AdminTaskFlowDay {
  date: string;
  projectId: string;
  created: number;
  scheduled: number;
  scheduledCompleted: number;
  scheduledSkipped: number;
  resolvedCompleted: number;
  resolvedSkipped: number;
}

/** One completed task's cycle time, keyed by its completion day. */
export interface AdminCycleSample {
  date: string;
  projectId: string;
  hours: number;
}

export interface AdminAnalytics {
  window: { from: string; to: string };
  projects: AdminAnalyticsProject[];
  engineers: AdminAnalyticsEngineer[];
  dailyActivity: AdminDailyActivity[];
  peakByState: AdminPeakByState[];
  peakByEngineer: AdminPeakByEngineer[];
  taskFlow: AdminTaskFlowDay[];
  cycleSamples: AdminCycleSample[];
  lastUpdated: string;
}

/** One day of engineer submission activity (drawer heatmap cell). */
export interface OmsEngineerActivityDay {
  /** Calendar day, YYYY-MM-DD. */
  date: string;
  pass: number;
  fail: number;
  total: number;
}

/** Per-day submission counts for one engineer inside a window. */
export interface OmsEngineerActivity {
  resourceId: string;
  from: string;
  to: string;
  /** Only days with at least one submission are listed. */
  days: OmsEngineerActivityDay[];
  total: number;
  lastUpdated: string;
}

export interface DashboardFilters {
  from?: string; // ISO date string
  to?: string;   // ISO date string
  projectIds?: string[];
  locationIds?: string[];
  roleIds?: string[];
  refresh?: boolean;
}

/** ===== SuperAdmin Dashboard ===== */

export type SuperAdminBucket =
  | 'completed'
  | 'readyToStart'
  | 'onTrack'
  | 'atRisk'
  | 'draftIncomplete';

export interface SuperAdminBucketKPI {
  bucket: SuperAdminBucket;
  label: string;
  value: number;
  delta: number;
  trend: 'up' | 'down' | 'flat';
  description: string;
  spark: number[];
}

export interface SuperAdminOverview {
  buckets: SuperAdminBucketKPI[];
  totals: {
    totalProjects: number;
    activeUsers: number;
    totalSites: number;
    pendingApprovals: number;
  };
  pipelineFunnel: Array<{ stage: string; count: number; conversion: number }>;
  complianceHeatmap: {
    dates: string[];
    rows: Array<{ projectId: string; projectName: string; values: number[] }>;
  };
  topPerformers: Array<{
    userId: string;
    name: string;
    role: string;
    score: number;
    projectsCount: number;
    completedTasks: number;
    avatar?: string;
  }>;
  laggingProjects: Array<{
    projectId: string;
    projectName: string;
    code: string;
    manager: string;
    pendingTasks: number;
    daysSinceUpdate: number | null;
    healthScore: number;
    riskLevel: 'low' | 'medium' | 'high' | 'critical';
  }>;
  rolePerformance: Array<{
    role: string;
    totalUsers: number;
    activeUsers: number;
    avgScore: number;
    completedThisWeek: number;
  }>;
  activityFeed: Array<{
    id: string;
    type: string;
    message: string;
    actor: string;
    timestamp: string;
    relativeTime: string;
    severity: 'info' | 'success' | 'warning' | 'critical';
  }>;
  thresholds: {
    onTrackComplianceMin: number;
    atRiskComplianceMax: number;
    inactiveDays: number;
    highPendingThreshold: number;
  };
  lastUpdated: string;
}

export interface SuperAdminPortfolioProject {
  projectId: string;
  code: string;
  projectName: string;
  customer: string;
  location: string;
  department: string;
  manager: string;
  priority: string;
  progress: number;
  bucket: SuperAdminBucket;
  healthScore: number;
  totalTasks: number;
  completedTasks: number;
  pendingTasks: number;
  overdueTasks: number;
  failedTasks: number;
  resourceCount: number;
  complianceLast7d: number;
  lastActivityAt: string | null;
  daysSinceUpdate: number | null;
}

export interface SuperAdminPortfolio {
  total: number;
  byBucket: Record<SuperAdminBucket, number>;
  projects: SuperAdminPortfolioProject[];
  lastUpdated: string;
}

export interface SuperAdminProjectDetail {
  project: {
    projectId: string;
    code: string;
    projectName: string;
    customer: string;
    manager: string;
    location: string;
    department: string;
    priority: string;
    status: string;
    bucket: SuperAdminBucket;
    healthScore: number;
    progress: number;
    plannedStart: string | null;
    plannedEnd: string | null;
    actualStart: string | null;
  };
  taskBreakdown: {
    total: number;
    completed: number;
    inProgress: number;
    pending: number;
    overdue: number;
    failed: number;
    skipped: number;
  };
  sCurve: Array<{ date: string; planned: number; actual: number }>;
  complianceHeatmap: { dates: string[]; values: number[] };
  team: {
    head: { userId: string; name: string } | null;
    manager: { userId: string; name: string } | null;
    supervisors: Array<{ userId: string; name: string; score: number }>;
    engineers: Array<{
      userId: string;
      name: string;
      score: number;
      uploadsLast7d: number;
      passRate: number;
      lastActivityAt: string | null;
      status: 'active' | 'idle' | 'inactive';
    }>;
  };
  recentActivity: Array<{
    id: string;
    type: string;
    actor: string;
    message: string;
    timestamp: string;
    relativeTime: string;
  }>;
  issues: Array<{
    id: string;
    title: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    ageDays: number;
    owner: string;
  }>;
  lastUpdated: string;
}

export interface SuperAdminUserPerformance {
  user: {
    userId: string;
    name: string;
    email: string;
    role: string;
    department: string;
    avatar?: string;
    joinedAt: string | null;
    lastLoginAt: string | null;
    isOnline: boolean;
  };
  score: {
    overall: number;
    completionRate: number;
    onTimeRate: number;
    reportingConsistency: number;
    qualityRate: number;
    vsRoleAverage: number;
  };
  punchCard: number[][];
  weeklyVelocity: Array<{ weekStart: string; label: string; assigned: number; completed: number }>;
  monthlyVelocity: Array<{ monthStart: string; label: string; assigned: number; completed: number }>;
  streak: { current: number; longest: number; lastReportedAt: string | null };
  projects: Array<{
    projectId: string;
    projectName: string;
    role: string;
    tasksAssigned: number;
    tasksCompleted: number;
  }>;
  recentSubmissions: Array<{
    id: string;
    taskTitle: string;
    projectName: string;
    status: 'pass' | 'fail' | 'pending';
    submittedAt: string;
    relativeTime: string;
    photoCount: number;
  }>;
  lastUpdated: string;
}

export interface SuperAdminFilters {
  projectIds?: string[];
  locationIds?: string[];
  departmentIds?: string[];
  bucket?: SuperAdminBucket;
  search?: string;
}

// Dashboard service implementation
export const dashboardService = {
  /**
   * Get comprehensive dashboard data
   */
  getDashboard: async (filters?: DashboardFilters): Promise<DashboardData> => {
    try {
      const params = new URLSearchParams();
      
      if (filters?.from) params.append('from', filters.from);
      if (filters?.to) params.append('to', filters.to);
      if (filters?.projectIds?.length) {
        filters.projectIds.forEach(id => params.append('projectIds', id));
      }
      if (filters?.locationIds?.length) {
        filters.locationIds.forEach(id => params.append('locationIds', id));
      }
      if (filters?.roleIds?.length) {
        filters.roleIds.forEach(id => params.append('roleIds', id));
      }
      if (filters?.refresh) params.append('refresh', 'true');

      const queryString = params.toString();
      const url = `/dashboard${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get<ApiResponse<DashboardData>>(url);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch dashboard data');
      }
      
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching dashboard data:', error);
      throw new Error(error.message || 'Failed to fetch dashboard data');
    }
  },

  /**
   * Get system statistics only
   */
  getSystemStats: async (filters?: Pick<DashboardFilters, 'from' | 'to'>): Promise<{
    systemStats: SystemStats;
    lastUpdated: string;
  }> => {
    try {
      const params = new URLSearchParams();
      
      if (filters?.from) params.append('from', filters.from);
      if (filters?.to) params.append('to', filters.to);

      const queryString = params.toString();
      const url = `/dashboard/stats${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get<ApiResponse<{
        systemStats: SystemStats;
        lastUpdated: string;
      }>>(url);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch system stats');
      }
      
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching system stats:', error);
      throw new Error(error.message || 'Failed to fetch system stats');
    }
  },

  /**
   * Get user analytics data
   */
  getUserAnalytics: async (filters?: Pick<DashboardFilters, 'from' | 'to' | 'roleIds'>): Promise<{
    userGrowthData: UserGrowthData[];
    userAnalytics: UserAnalytics;
    lastUpdated: string;
  }> => {
    try {
      const params = new URLSearchParams();
      
      if (filters?.from) params.append('from', filters.from);
      if (filters?.to) params.append('to', filters.to);
      if (filters?.roleIds?.length) {
        filters.roleIds.forEach(id => params.append('roleIds', id));
      }

      const queryString = params.toString();
      const url = `/dashboard/users${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get<ApiResponse<{
        userGrowthData: UserGrowthData[];
        userAnalytics: UserAnalytics;
        lastUpdated: string;
      }>>(url);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch user analytics');
      }
      
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching user analytics:', error);
      throw new Error(error.message || 'Failed to fetch user analytics');
    }
  },

  /**
   * Get project and asset analytics
   */
  getProjectAnalytics: async (filters?: Pick<DashboardFilters, 'from' | 'to' | 'projectIds' | 'locationIds'>): Promise<{
    assetDistribution: AssetDistribution[];
    projectStatus: ProjectStatus[];
    lastUpdated: string;
  }> => {
    try {
      const params = new URLSearchParams();
      
      if (filters?.from) params.append('from', filters.from);
      if (filters?.to) params.append('to', filters.to);
      if (filters?.projectIds?.length) {
        filters.projectIds.forEach(id => params.append('projectIds', id));
      }
      if (filters?.locationIds?.length) {
        filters.locationIds.forEach(id => params.append('locationIds', id));
      }

      const queryString = params.toString();
      const url = `/dashboard/projects${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get<ApiResponse<{
        assetDistribution: AssetDistribution[];
        projectStatus: ProjectStatus[];
        lastUpdated: string;
      }>>(url);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch project analytics');
      }
      
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching project analytics:', error);
      throw new Error(error.message || 'Failed to fetch project analytics');
    }
  },

  /**
   * Get operational analytics (tasks and checkpoints)
   */
  getOperationalAnalytics: async (filters?: Pick<DashboardFilters, 'from' | 'to' | 'projectIds'>): Promise<{
    taskAnalytics: TaskAnalytics;
    checkpointAnalytics: CheckpointAnalytics;
    lastUpdated: string;
  }> => {
    try {
      const params = new URLSearchParams();
      
      if (filters?.from) params.append('from', filters.from);
      if (filters?.to) params.append('to', filters.to);
      if (filters?.projectIds?.length) {
        filters.projectIds.forEach(id => params.append('projectIds', id));
      }

      const queryString = params.toString();
      const url = `/dashboard/operations${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get<ApiResponse<{
        taskAnalytics: TaskAnalytics;
        checkpointAnalytics: CheckpointAnalytics;
        lastUpdated: string;
      }>>(url);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch operational analytics');
      }
      
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching operational analytics:', error);
      throw new Error(error.message || 'Failed to fetch operational analytics');
    }
  },

  /**
   * Get recent activities
   */
  getRecentActivities: async (limit: number = 20): Promise<{
    recentActivities: RecentActivity[];
    lastUpdated: string;
  }> => {
    try {
      const response = await api.get<ApiResponse<{
        recentActivities: RecentActivity[];
        lastUpdated: string;
      }>>(`/dashboard/activities?limit=${limit}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch recent activities');
      }
      
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching recent activities:', error);
      throw new Error(error.message || 'Failed to fetch recent activities');
    }
  },

  /**
   * Get performance metrics
   */
  getPerformanceMetrics: async (refresh?: boolean): Promise<{
    performanceMetrics: PerformanceMetrics;
    lastUpdated: string;
  }> => {
    try {
      const url = `/dashboard/performance${refresh ? '?refresh=true' : ''}`;
      
      const response = await api.get<ApiResponse<{
        performanceMetrics: PerformanceMetrics;
        lastUpdated: string;
      }>>(url);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch performance metrics');
      }
      
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching performance metrics:', error);
      throw new Error(error.message || 'Failed to fetch performance metrics');
    }
  },

  /**
   * Utility function to get date range for common periods
   */
  getDateRange: (period: 'today' | 'week' | 'month' | 'quarter' | 'year' | 'custom', customFrom?: Date, customTo?: Date): {
    from: string;
    to: string;
  } => {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let from: Date;
    let to: Date = now;

    switch (period) {
      case 'today':
        from = startOfDay;
        break;
      case 'week':
        from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'quarter':
        const quarterStart = Math.floor(now.getMonth() / 3) * 3;
        from = new Date(now.getFullYear(), quarterStart, 1);
        break;
      case 'year':
        from = new Date(now.getFullYear(), 0, 1);
        break;
      case 'custom':
        from = customFrom || new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        to = customTo || now;
        break;
      default:
        from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    }

    return {
      from: from.toISOString(),
      to: to.toISOString()
    };
  },

  /**
   * Manager dashboard (demo payload fills KPIs, charts, approvals & activity).
   * Head dashboard calls the same method — `headSnapshot` is ignored by Manager UI.
   */
  getManagerDashboard: async (): Promise<ManagerDashboardData> => {
    const emptyBreakdown: ProjectStatusBreakdown = {
      good: 0,
      attention: 0,
      critical: 0,
      total: 0
    };
    const emptyMaintenance: MaintenanceAggregateStats = {
      totalPass: 0,
      totalFail: 0,
      totalOverdue: 0,
      totalSkipped: 0,
      totalInProgress: 0,
      totalPending: 0,
      totalOpenIssues: 0,
      passRate: 0,
      failRate: 0
    };
    try {
      const response = await api.get<ApiResponse<ManagerDashboardData>>('/dashboard/manager');

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch manager dashboard data');
      }

      const data = response.data.data;
      // Older API builds may omit extended project-health fields — keep UI stable.
      return {
        ...data,
        projects: data.projects ?? [],
        projectStatusBreakdown: data.projectStatusBreakdown ?? emptyBreakdown,
        resourceLoadByProject: data.resourceLoadByProject ?? [],
        criticalProjects: data.criticalProjects ?? [],
        maintenanceStats: data.maintenanceStats ?? emptyMaintenance
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch manager dashboard data';
      console.error('Error fetching manager dashboard data:', error);
      throw new Error(message);
    }
  },

  /**
   * Advanced Manager Dashboard (v2): 6-KPI overview
   */
  getManagerOverview: async (filters?: ManagerFilters): Promise<ManagerOverview> => {
    const params = new URLSearchParams();
    if (filters?.projectIds?.length) params.append('projectIds', filters.projectIds.join(','));
    if (filters?.locationIds?.length) params.append('locationIds', filters.locationIds.join(','));
    if (filters?.departmentIds?.length) params.append('departmentIds', filters.departmentIds.join(','));
    const qs = params.toString();
    const url = `/dashboard/manager/overview${qs ? `?${qs}` : ''}`;
    const response = await api.get<ApiResponse<ManagerOverview>>(url);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to load overview');
    return response.data.data;
  },

  /**
   * Advanced Manager Dashboard (v2): weekly progress trend
   */
  getWeeklyProgress: async (filters?: ManagerFilters & { weeks?: number }): Promise<WeeklyProgressResponse> => {
    const params = new URLSearchParams();
    if (filters?.projectIds?.length) params.append('projectIds', filters.projectIds.join(','));
    if (filters?.locationIds?.length) params.append('locationIds', filters.locationIds.join(','));
    if (filters?.departmentIds?.length) params.append('departmentIds', filters.departmentIds.join(','));
    if (filters?.weeks) params.append('weeks', String(filters.weeks));
    const qs = params.toString();
    const url = `/dashboard/manager/weekly-progress${qs ? `?${qs}` : ''}`;
    const response = await api.get<ApiResponse<WeeklyProgressResponse>>(url);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to load weekly progress');
    return response.data.data;
  },

  /**
   * Advanced Manager Dashboard (v2): drill-down for a specific KPI bucket
   */
  getManagerDrillDown: async (bucket: ManagerOverviewBucket, filters?: ManagerFilters): Promise<DrillDownResponse> => {
    const params = new URLSearchParams();
    if (filters?.projectIds?.length) params.append('projectIds', filters.projectIds.join(','));
    if (filters?.locationIds?.length) params.append('locationIds', filters.locationIds.join(','));
    if (filters?.departmentIds?.length) params.append('departmentIds', filters.departmentIds.join(','));
    const qs = params.toString();
    const url = `/dashboard/manager/drilldown/${bucket}${qs ? `?${qs}` : ''}`;
    const response = await api.get<ApiResponse<DrillDownResponse>>(url);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to load drill-down');
    return response.data.data;
  },

  /**
   * Advanced Manager Dashboard (v2): smart chatbot quick-query
   */
  postManagerChatbot: async (intent: ManagerChatbotIntent): Promise<ManagerChatbotResponse> => {
    const response = await api.post<ApiResponse<ManagerChatbotResponse>>('/dashboard/manager/chatbot', { intent });
    if (!response.data.success) throw new Error(response.data.message || 'Failed to query chatbot');
    return response.data.data;
  },

  /** OMS Manager Dashboard — Layer 1 + Layer 2 KPIs */
  getOmsLayered: async (): Promise<OmsLayeredOverview> => {
    const response = await api.get<ApiResponse<OmsLayeredOverview>>('/dashboard/manager/oms/layered');
    if (!response.data.success) throw new Error(response.data.message || 'Failed to load OMS layered overview');
    return response.data.data;
  },

  /** OMS Manager Dashboard — Layer 3 execution overview (DPR activities per project). */
  getOmsExecution: async (): Promise<OmsExecutionOverview> => {
    const response = await api.get<ApiResponse<OmsExecutionOverview>>('/dashboard/manager/oms/execution');
    if (!response.data.success) throw new Error(response.data.message || 'Failed to load OMS execution overview');
    return response.data.data;
  },

  /** OMS-only project picker list (id, name, code). */
  getOmsProjectsForPicker: async (): Promise<OmsProjectPickerItem[]> => {
    const response = await api.get<ApiResponse<OmsProjectPickerItem[]>>('/dashboard/manager/oms/projects-picker');
    if (!response.data.success) throw new Error(response.data.message || 'Failed to load OMS projects');
    return response.data.data;
  },

  /**
   * OMS Manager Dashboard — Layer 4 resources overview (filtered by date range).
   * Pass `from`/`to` (YYYY-MM-DD) with range='custom' for an explicit window.
   */
  getOmsResources: async (
    range: OmsResourcesRange = 'all',
    window?: { from: string; to: string }
  ): Promise<OmsResourcesOverview> => {
    const params = new URLSearchParams({ range });
    if (range === 'custom' && window) {
      params.set('from', window.from);
      params.set('to', window.to);
    }
    const url = `/dashboard/manager/oms/resources?${params.toString()}`;
    const response = await api.get<ApiResponse<OmsResourcesOverview>>(url);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to load OMS resources');
    return response.data.data;
  },

  /** Drill-down for any OMS layered KPI bucket. */
  getOmsDrillDown: async (bucket: OmsDrillBucket): Promise<OmsDrillDownResponse> => {
    const response = await api.get<ApiResponse<OmsDrillDownResponse>>(`/dashboard/manager/oms/drilldown/${bucket}`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to load OMS drill-down');
    return response.data.data;
  },

  /** OMS Operations Dashboard — consolidated overview (unified Manager + Head). */
  getOmsOverview: async (range: OmsResourcesRange = 'all'): Promise<OmsOverviewResponse> => {
    const response = await api.get<ApiResponse<OmsOverviewResponse>>(
      `/dashboard/oms/overview?range=${encodeURIComponent(range)}`
    );
    if (!response.data.success) throw new Error(response.data.message || 'Failed to load OMS overview');
    return response.data.data;
  },

  /** Admin Analytics — platform-health raw slices for a day window. */
  getAdminAnalytics: async (window: { from: string; to: string }): Promise<AdminAnalytics> => {
    const params = new URLSearchParams({ from: window.from, to: window.to });
    const response = await api.get<ApiResponse<AdminAnalytics>>(
      `/dashboard/admin/analytics?${params.toString()}`
    );
    if (!response.data.success) throw new Error(response.data.message || 'Failed to load admin analytics');
    return response.data.data;
  },

  /** Per-day submission activity for one engineer (drawer heatmap). */
  getOmsEngineerActivity: async (
    resourceId: string,
    window: { from: string; to: string }
  ): Promise<OmsEngineerActivity> => {
    const params = new URLSearchParams({ from: window.from, to: window.to });
    const response = await api.get<ApiResponse<OmsEngineerActivity>>(
      `/dashboard/oms/engineers/${encodeURIComponent(resourceId)}/activity?${params.toString()}`
    );
    if (!response.data.success) throw new Error(response.data.message || 'Failed to load engineer activity');
    return response.data.data;
  },

  /** Per-location OMS dashboard (projects, workload and totals at one site). */
  getOmsLocationOverview: async (locationId: string): Promise<OmsLocationOverview> => {
    const response = await api.get<ApiResponse<OmsLocationOverview>>(
      `/dashboard/oms/locations/${encodeURIComponent(locationId)}`
    );
    if (!response.data.success) throw new Error(response.data.message || 'Failed to load location overview');
    return response.data.data;
  },

  /**
   * SuperAdmin Dashboard: top-level overview (5 KPI buckets, funnel, heatmap...)
   */
  getSuperAdminOverview: async (filters?: SuperAdminFilters): Promise<SuperAdminOverview> => {
    const params = new URLSearchParams();
    if (filters?.projectIds?.length) params.append('projectIds', filters.projectIds.join(','));
    if (filters?.locationIds?.length) params.append('locationIds', filters.locationIds.join(','));
    if (filters?.departmentIds?.length) params.append('departmentIds', filters.departmentIds.join(','));
    const qs = params.toString();
    const url = `/dashboard/super-admin/overview${qs ? `?${qs}` : ''}`;
    const response = await api.get<ApiResponse<SuperAdminOverview>>(url);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to load SuperAdmin overview');
    return response.data.data;
  },

  /**
   * SuperAdmin Dashboard: filterable project portfolio (L2).
   */
  getSuperAdminPortfolio: async (filters?: SuperAdminFilters): Promise<SuperAdminPortfolio> => {
    const params = new URLSearchParams();
    if (filters?.bucket) params.append('bucket', filters.bucket);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.projectIds?.length) params.append('projectIds', filters.projectIds.join(','));
    if (filters?.locationIds?.length) params.append('locationIds', filters.locationIds.join(','));
    if (filters?.departmentIds?.length) params.append('departmentIds', filters.departmentIds.join(','));
    const qs = params.toString();
    const url = `/dashboard/super-admin/portfolio${qs ? `?${qs}` : ''}`;
    const response = await api.get<ApiResponse<SuperAdminPortfolio>>(url);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to load portfolio');
    return response.data.data;
  },

  /**
   * SuperAdmin Dashboard: per-project deep-dive (L3).
   */
  getSuperAdminProjectDetail: async (projectId: string): Promise<SuperAdminProjectDetail> => {
    const response = await api.get<ApiResponse<SuperAdminProjectDetail>>(
      `/dashboard/super-admin/projects/${projectId}`
    );
    if (!response.data.success) throw new Error(response.data.message || 'Failed to load project detail');
    return response.data.data;
  },

  /**
   * SuperAdmin Dashboard: per-user performance (L4).
   */
  getSuperAdminUserPerformance: async (userId: string): Promise<SuperAdminUserPerformance> => {
    const response = await api.get<ApiResponse<SuperAdminUserPerformance>>(
      `/dashboard/super-admin/users/${userId}`
    );
    if (!response.data.success) throw new Error(response.data.message || 'Failed to load user performance');
    return response.data.data;
  },

  /**
   * Get admin dashboard: Manager & Head user activity tracking
   */
  getAdminManagerHeadActivity: async (params?: { limit?: number }): Promise<AdminManagerHeadActivity> => {
    try {
      const limit = params?.limit ?? 50;
      const response = await api.get<ApiResponse<AdminManagerHeadActivity>>(
        `/dashboard/admin/manager-head-activity?limit=${limit}`
      );
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch admin activity data');
      }
      return response.data.data;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch admin activity data';
      console.error('Error fetching admin manager/head activity:', error);
      throw new Error(message);
    }
  },

  /**
   * User management dashboard (expects backend route when implemented).
   */
  getUserManagementDashboard: async (params?: { refresh?: boolean }): Promise<UserManagementDashboardData> => {
    try {
      const search = new URLSearchParams();
      if (params?.refresh) search.append('refresh', 'true');
      const q = search.toString();
      const response = await api.get<ApiResponse<UserManagementDashboardData>>(
        `/dashboard/user-management${q ? `?${q}` : ''}`
      );
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch user management dashboard');
      }
      return response.data.data;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch user management dashboard';
      console.error('Error fetching user management dashboard:', error);
      throw new Error(message);
    }
  },
};
